import { Cartesian2, Cartesian3 } from "cesium";
import { HALinearSegment } from "./horizontal-alignment-linear-segment";
import { CurveDataType, HorizontalAlignmentType, } from "./interface";
import { HAQuadraticBezier } from "./horizontal-alignment-quadratic-bezier";
import { HAThreePointArc } from "./horizontal-alignment-three-point-arc";
import * as HAUtils from "./horizontal-alignment-utils";
// Only support for old data
// Deprecated
export class QuadraticBezierHorizontalAlignment {
    type = HorizontalAlignmentType.QUADRATIC_BEZIER;
    // -------------------- Private Instance Variables --------------------
    _rootHACurve;
    _points;
    _length;
    constructor() {
        this._rootHACurve = undefined;
        this._points = [];
        this._length = 0;
    }
    //-------------------- Public Getters/Setters --------------------
    get points() {
        return this._points;
    }
    get segments() {
        return this._rootHACurve?.toArray() || [];
    }
    get length() {
        return this._length;
    }
    //----------------------- Public Methods -----------------------
    dispose() {
        let currentSegment = this._rootHACurve;
        while (currentSegment) {
            const next = currentSegment.next;
            currentSegment.dispose();
            currentSegment = next;
        }
        this._rootHACurve = undefined;
        this._points.length = 0;
        this._length = 0;
    }
    recompute(ipPoints) {
        this.dispose();
        this.initializeFromIPPoints(ipPoints);
    }
    initializeFromIPPoints(ipPoints) {
        this.dispose();
        if (ipPoints.length < 2) {
            throw new Error("At least two IP points are required");
        }
        const { curves, newPoints } = this.generateQuadraticAlignmentFromIPs(ipPoints.map((ip) => ({ id: ip.id, localPoint: ip.localPoint })));
        const surveyPointsMap = new Map();
        ipPoints.forEach((ip) => surveyPointsMap.set(ip.id, ip));
        newPoints.forEach((p) => surveyPointsMap.set(p.id, {
            id: p.id,
            localPoint: p.localPoint,
        }));
        this.initializeFromCurveData(curves, surveyPointsMap);
        this.postCompute();
        return this._rootHACurve;
    }
    initializeFromCurveData(curveData, surveyPointsMap) {
        this.dispose();
        if (curveData.length === 0) {
            throw new Error("Curve data is empty");
        }
        const getSegment = (data, _index) => {
            switch (data.type) {
                case CurveDataType.LINEAR:
                    const startPoint = surveyPointsMap.get(data.startPointId);
                    const endPoint = surveyPointsMap.get(data.endPointId);
                    if (!startPoint || !endPoint) {
                        throw new Error("Survey point not found");
                    }
                    return new HALinearSegment(startPoint, endPoint);
                case CurveDataType.QUADRATIC_BEZIER:
                    const quadraticBezierPoints = data.pointIds.map((p) => surveyPointsMap.get(p));
                    if (quadraticBezierPoints.some((p) => !p)) {
                        throw new Error("Survey point not found");
                    }
                    return new HAQuadraticBezier(quadraticBezierPoints);
                case CurveDataType.THREE_POINT_ARC:
                    const tpaStartPoint = surveyPointsMap.get(data.startPointId);
                    const tpaThroughPoint = surveyPointsMap.get(data.throughPointId);
                    const tpaEndPoint = surveyPointsMap.get(data.endPointId);
                    if (!tpaStartPoint || !tpaThroughPoint || !tpaEndPoint) {
                        throw new Error("Survey point not found");
                    }
                    return new HAThreePointArc(tpaStartPoint, tpaThroughPoint, tpaEndPoint);
                default:
                    throw new Error(`Unknown curve type: ${data.type}`);
            }
        };
        this._rootHACurve = getSegment(curveData[0], 0);
        let currentSegment = this._rootHACurve;
        for (let i = 1; i < curveData.length; i++) {
            const segment = getSegment(curveData[i], i);
            currentSegment.insertAfter(segment);
            currentSegment = segment;
        }
        this.postCompute();
    }
    getClosestPointInfo(point) {
        return HAUtils.getClosestPointInfo(point, this);
    }
    getInfoAtDistanceInBatch(sortedDistanceFromStart) {
        return HAUtils.getInfoAtDistanceInBatch(sortedDistanceFromStart, this);
    }
    getPointInfoAtDistance(distanceFromStart) {
        return HAUtils.getPointInfoAtDistance(distanceFromStart, this);
    }
    getDistanceFromStart(point) {
        return HAUtils.getDistanceFromStart(point, this);
    }
    // -------------------------- Private Methods --------------------------
    computePoints() {
        this._points.length = 0;
        let currentSegment = this._rootHACurve;
        while (currentSegment) {
            this._points.push(...currentSegment.curvePoints);
            currentSegment = currentSegment.next;
        }
    }
    computeLength() {
        this._length = 0;
        for (let i = 0; i < this.points.length - 1; i++) {
            this._length += Cartesian2.distance(this.points[i], this.points[i + 1]);
        }
    }
    postCompute() {
        this.computePoints();
        this.computeLength();
    }
    generateQuadraticAlignmentFromIPs(ipPoints) {
        if (ipPoints.length < 2) {
            throw new Error("At least two IP points are required");
        }
        if (ipPoints.length === 2) {
            const linearSeg = {
                type: CurveDataType.LINEAR,
                startPointId: ipPoints[0].id,
                endPointId: ipPoints[1].id,
            };
            return { curves: [linearSeg], newPoints: [] };
        }
        // linear --> quadratic-->linear
        const curves = [];
        const newPoints = [];
        const firstMidPoint = Cartesian3.midpoint(ipPoints[0].localPoint, ipPoints[1].localPoint, new Cartesian3());
        const lastMidPoint = Cartesian3.midpoint(ipPoints[ipPoints.length - 1].localPoint, ipPoints[ipPoints.length - 2].localPoint, new Cartesian3());
        const firstMidPointId = `BC_${ipPoints[0].id}`;
        const lastMidPointId = `EC_${ipPoints[ipPoints.length - 1].id}`;
        newPoints.push({ id: firstMidPointId, localPoint: firstMidPoint });
        newPoints.push({ id: lastMidPointId, localPoint: lastMidPoint });
        // linear --> quadratic-->linear
        const firstLinearSeg = {
            type: CurveDataType.LINEAR,
            startPointId: ipPoints[0].id,
            endPointId: firstMidPointId,
        };
        const quadraticIpPoints = ipPoints.slice(1, ipPoints.length - 1);
        const quadraticBezierSeg = {
            type: CurveDataType.QUADRATIC_BEZIER,
            pointIds: [
                firstMidPointId,
                ...quadraticIpPoints.map((ip) => ip.id),
                lastMidPointId,
            ],
            startPointId: firstMidPointId,
            endPointId: lastMidPointId,
        };
        const lastLinearSeg = {
            type: CurveDataType.LINEAR,
            startPointId: lastMidPointId,
            endPointId: ipPoints[ipPoints.length - 1].id,
        };
        curves.push(firstLinearSeg);
        curves.push(quadraticBezierSeg);
        curves.push(lastLinearSeg);
        return { curves, newPoints };
    }
}
//# sourceMappingURL=quadratic-bezier-horizontal-alignment.js.map